import { useCallback, useEffect, useState } from "react";
import {
  createRole,
  deleteRole,
  getRole,
  listPermissions,
  listRoles,
  replaceRolePermissions,
  updateRole,
} from "../../../../api/adminRolesApi";
import AdminStatePanel from "../layout/AdminStatePanel";
import AdminTableContainer from "../layout/AdminTableContainer";
import RequirePermission from "../../../../auth/RequirePermission";
import StatusBadge from "../../intelligence/StatusBadge";
import { formatDateTime, formatShortDate } from "../users/adminUserFormatters";

function suggestRoleCode(roleName) {
  return String(roleName || "")
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .replace(/_+/g, "_");
}

function ModalBackdrop({ children, onClose, title, wide = false }) {
  return (
    <div className="admin-re-modal-backdrop" role="presentation" onClick={onClose}>
      <div
        className={`admin-re-modal panel-card ${wide ? "admin-re-modal--wide" : ""}`}
        role="dialog"
        aria-labelledby="admin-role-modal-title"
        onClick={(event) => event.stopPropagation()}
      >
        <header className="admin-re-modal-header">
          <div>
            <p className="eyebrow">Roles</p>
            <h2 id="admin-role-modal-title">{title}</h2>
          </div>
          <button type="button" className="midpoint-share-button" onClick={onClose}>
            Close
          </button>
        </header>
        <div className="admin-re-modal-body">{children}</div>
      </div>
    </div>
  );
}

function PermissionPicker({ groups, selected, onChange, disabled = false, search = "" }) {
  const normalizedSearch = search.trim().toLowerCase();

  return (
    <div className="admin-roles-permissions">
      {groups.map((group) => {
        const visiblePermissions = group.permissions.filter((permission) => {
          if (!normalizedSearch) {
            return true;
          }

          const haystack = `${permission.permissionCode} ${permission.permissionName} ${permission.description || ""}`.toLowerCase();
          return haystack.includes(normalizedSearch);
        });

        if (!visiblePermissions.length) {
          return null;
        }

        const allSelected = visiblePermissions.every((permission) =>
          selected.includes(permission.permissionCode)
        );

        return (
          <fieldset key={group.category} className="admin-roles-permissions__group">
            <legend>
              {group.category}
              {!disabled ? (
                <button
                  type="button"
                  className="admin-re-inline-link admin-roles-permissions__toggle"
                  onClick={() => {
                    if (allSelected) {
                      onChange(
                        selected.filter(
                          (code) =>
                            !visiblePermissions.some((permission) => permission.permissionCode === code)
                        )
                      );
                    } else {
                      const merged = new Set(selected);
                      visiblePermissions.forEach((permission) =>
                        merged.add(permission.permissionCode)
                      );
                      onChange([...merged]);
                    }
                  }}
                >
                  {allSelected ? "Clear group" : "Select group"}
                </button>
              ) : null}
            </legend>
            {visiblePermissions.map((permission) => (
              <label key={permission.permissionCode} className="admin-users-checkbox">
                <input
                  type="checkbox"
                  disabled={disabled}
                  checked={selected.includes(permission.permissionCode)}
                  onChange={() => {
                    if (selected.includes(permission.permissionCode)) {
                      onChange(selected.filter((code) => code !== permission.permissionCode));
                    } else {
                      onChange([...selected, permission.permissionCode]);
                    }
                  }}
                />
                <span>
                  <strong>{permission.permissionName}</strong>
                  <span className="muted admin-users-role-desc"> — {permission.permissionCode}</span>
                </span>
                {permission.description ? (
                  <span className="muted admin-users-role-desc">{permission.description}</span>
                ) : null}
              </label>
            ))}
          </fieldset>
        );
      })}
    </div>
  );
}

const AdminRolesPageContent = ({ onClose }) => {
  const [roles, setRoles] = useState([]);
  const [permissionGroups, setPermissionGroups] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [modalError, setModalError] = useState("");
  const [modalSaving, setModalSaving] = useState(false);

  const [createOpen, setCreateOpen] = useState(false);
  const [detailRole, setDetailRole] = useState(null);
  const [editRole, setEditRole] = useState(null);
  const [permissionsRole, setPermissionsRole] = useState(null);

  const [createForm, setCreateForm] = useState({
    roleName: "",
    roleCode: "",
    description: "",
    permissions: [],
  });
  const [editForm, setEditForm] = useState({
    roleName: "",
    description: "",
    isActive: true,
  });
  const [permissionSelection, setPermissionSelection] = useState([]);
  const [permissionSearch, setPermissionSearch] = useState("");
  const [permissionsDirty, setPermissionsDirty] = useState(false);

  const loadData = useCallback(async () => {
    try {
      setIsLoading(true);
      setErrorMessage("");
      const [rolesBody, permissionsBody] = await Promise.all([listRoles(), listPermissions()]);
      setRoles(rolesBody.roles || []);
      setPermissionGroups(permissionsBody.groups || []);
    } catch (error) {
      setRoles([]);
      setErrorMessage(error.message || "Unable to load roles.");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const openDetail = async (roleCode) => {
    try {
      setModalError("");
      const body = await getRole(roleCode);
      setDetailRole(body.role);
    } catch (error) {
      setErrorMessage(error.message || "Unable to load role detail.");
    }
  };

  const openEdit = async (role) => {
    if (role.isSystemRole) {
      return;
    }

    try {
      setModalError("");
      const body = await getRole(role.roleCode);
      setEditRole(body.role);
      setEditForm({
        roleName: body.role.roleName,
        description: body.role.description || "",
        isActive: body.role.isActive !== false,
      });
    } catch (error) {
      setErrorMessage(error.message || "Unable to load role.");
    }
  };

  const openPermissions = async (role) => {
    try {
      setModalError("");
      setPermissionSearch("");
      const body = await getRole(role.roleCode);
      setPermissionsRole(body.role);
      setPermissionSelection(
        (body.role.permissions || []).map((permission) => permission.permissionCode)
      );
      setPermissionsDirty(false);
    } catch (error) {
      setErrorMessage(error.message || "Unable to load role permissions.");
    }
  };

  const handleCreateSubmit = async (event) => {
    event.preventDefault();
    setModalSaving(true);
    setModalError("");

    try {
      await createRole(createForm);
      setCreateOpen(false);
      setCreateForm({ roleName: "", roleCode: "", description: "", permissions: [] });
      setRoleCodeTouched(false);
      setSuccessMessage("Role created successfully.");
      await loadData();
    } catch (error) {
      setModalError(error.message || "Unable to create role.");
    } finally {
      setModalSaving(false);
    }
  };

  const handleEditSubmit = async (event) => {
    event.preventDefault();
    if (!editRole) {
      return;
    }

    setModalSaving(true);
    setModalError("");

    try {
      await updateRole(editRole.roleCode, editForm);
      setEditRole(null);
      setSuccessMessage("Role updated successfully.");
      await loadData();
    } catch (error) {
      setModalError(error.message || "Unable to update role.");
    } finally {
      setModalSaving(false);
    }
  };

  const handlePermissionsSave = async () => {
    if (!permissionsRole) {
      return;
    }

    if (
      permissionsRole.isSystemRole &&
      !window.confirm(
        "System role permissions are protected. This action should be blocked by the server."
      )
    ) {
      return;
    }

    setModalSaving(true);
    setModalError("");

    try {
      await replaceRolePermissions(permissionsRole.roleCode, permissionSelection);
      setPermissionsRole(null);
      setSuccessMessage("Role permissions updated.");
      await loadData();
    } catch (error) {
      setModalError(error.message || "Unable to save permissions.");
    } finally {
      setModalSaving(false);
    }
  };

  const handleDeleteRole = async (role) => {
    if (role.isSystemRole) {
      return;
    }

    if (!window.confirm(`Delete role ${role.roleName}? This cannot be undone.`)) {
      return;
    }

    try {
      await deleteRole(role.roleCode);
      setSuccessMessage("Role deleted.");
      await loadData();
    } catch (error) {
      setErrorMessage(error.message || "Unable to delete role.");
    }
  };

  const permissionsReadOnly = permissionsRole?.isSystemRole || permissionsRole?.roleCode === "SUPER_ADMIN";

  const [roleCodeTouched, setRoleCodeTouched] = useState(false);

  const systemCount = roles.filter((r) => r.isSystemRole).length;
  const customCount = roles.filter((r) => !r.isSystemRole).length;
  const totalPermissions = roles.reduce((sum, r) => sum + (r.permissionCount || 0), 0);

  return (
    <div className="admin-dashboard admin-roles-dash">
      <header className="admin-dashboard__header">
        <div>
          <h1 className="admin-dashboard__title">Roles & Permissions</h1>
          <p className="admin-dashboard__subtitle">
            View system roles and create custom roles with tailored permissions
          </p>
        </div>
        <div className="admin-dashboard__header-actions">
          <button
            type="button"
            className="admin-dashboard__action-btn admin-dashboard__action-btn--primary"
            onClick={() => setCreateOpen(true)}
          >
            Create Role
          </button>
          <button
            type="button"
            className="admin-dashboard__action-btn"
            onClick={loadData}
            disabled={isLoading}
          >
            Refresh
          </button>
          {onClose && (
            <button
              type="button"
              className="admin-dashboard__action-btn"
              onClick={onClose}
            >
              ← Dashboard
            </button>
          )}
        </div>
      </header>

      {!isLoading && !errorMessage && (
        <section className="admin-dashboard__kpis">
          <div className="admin-dash__kpi-card">
            <span className="admin-dash__kpi-label">Total Roles</span>
            <span className="admin-dash__kpi-value">{roles.length}</span>
          </div>
          <div className="admin-dash__kpi-card">
            <span className="admin-dash__kpi-label">System</span>
            <span className="admin-dash__kpi-value">{systemCount}</span>
          </div>
          <div className="admin-dash__kpi-card">
            <span className="admin-dash__kpi-label">Custom</span>
            <span className="admin-dash__kpi-value admin-dash__kpi-value--green">{customCount}</span>
          </div>
          <div className="admin-dash__kpi-card">
            <span className="admin-dash__kpi-label">Total Permissions</span>
            <span className="admin-dash__kpi-value">{totalPermissions}</span>
          </div>
        </section>
      )}

      {successMessage ? (
        <div className="admin-re-toast admin-re-toast--success" role="status">
          {successMessage}
          <button type="button" className="admin-re-toast__dismiss" onClick={() => setSuccessMessage("")}>
            ×
          </button>
        </div>
      ) : null}

      {isLoading ? <AdminStatePanel variant="loading" message="Loading roles…" /> : null}
      {!isLoading && errorMessage ? (
        <AdminStatePanel variant="error" message={errorMessage} onRetry={loadData} />
      ) : null}

      {!isLoading && !errorMessage ? (
        <section className="admin-dash__card admin-dash__card--wide admin-roles-dash__table-card">
          <AdminTableContainer>
            <table className="admin-data-health-table admin-users-table">
              <thead>
                <tr>
                  <th>Role</th>
                  <th>Code</th>
                  <th>Type</th>
                  <th>Status</th>
                  <th>Permissions</th>
                  <th>Users</th>
                  <th>Updated</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {roles.map((role) => (
                  <tr key={role.roleId}>
                    <td>
                      {role.roleName}
                      {role.roleCode === "SUPER_ADMIN" ? (
                        <StatusBadge label="Protected" tone="medium" />
                      ) : null}
                    </td>
                    <td>
                      <code>{role.roleCode}</code>
                    </td>
                    <td>
                      <StatusBadge
                        label={role.isSystemRole ? "System" : "Custom"}
                        tone={role.isSystemRole ? "neutral" : "confirmed"}
                      />
                    </td>
                    <td>
                      <StatusBadge
                        label={role.isActive === false ? "Inactive" : "Active"}
                        tone={role.isActive === false ? "disputed" : "confirmed"}
                      />
                    </td>
                    <td>{role.permissionCount}</td>
                    <td>{role.userCount}</td>
                    <td>{formatShortDate(role.updatedAt)}</td>
                    <td>
                      <div className="admin-users-actions">
                        <button type="button" className="admin-re-inline-link" onClick={() => openDetail(role.roleCode)}>
                          View
                        </button>
                        <button type="button" className="admin-re-inline-link" onClick={() => openPermissions(role)}>
                          Permissions
                        </button>
                        {!role.isSystemRole ? (
                          <>
                            <button type="button" className="admin-re-inline-link" onClick={() => openEdit(role)}>
                              Edit
                            </button>
                            <button
                              type="button"
                              className="admin-re-inline-link"
                              onClick={() => handleDeleteRole(role)}
                              disabled={role.userCount > 0}
                            >
                              Delete
                            </button>
                          </>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </AdminTableContainer>
        </section>
      ) : null}

      {createOpen ? (
        <ModalBackdrop title="Create custom role" wide onClose={() => setCreateOpen(false)}>
          <form className="admin-users-form" onSubmit={handleCreateSubmit}>
            <p className="muted admin-users-help">
              Custom roles let you grant a specific set of permissions without changing system roles.
            </p>
            {modalError ? <p className="admin-data-health-error">{modalError}</p> : null}
            <label className="admin-users-field">
              <span>Role name</span>
              <input
                required
                value={createForm.roleName}
                onChange={(event) => {
                  const roleName = event.target.value;
                  setCreateForm((form) => ({
                    ...form,
                    roleName,
                    roleCode: roleCodeTouched ? form.roleCode : suggestRoleCode(roleName),
                  }));
                }}
              />
            </label>
            <label className="admin-users-field">
              <span>Role code</span>
              <input
                required
                value={createForm.roleCode}
                onChange={(event) => {
                  setRoleCodeTouched(true);
                  setCreateForm((form) => ({ ...form, roleCode: suggestRoleCode(event.target.value) }));
                }}
              />
            </label>
            <label className="admin-users-field">
              <span>Description</span>
              <textarea
                rows={3}
                value={createForm.description}
                onChange={(event) =>
                  setCreateForm((form) => ({ ...form, description: event.target.value }))
                }
              />
            </label>
            <label className="admin-users-field">
              <span>Filter permissions</span>
              <input
                type="search"
                value={permissionSearch}
                onChange={(event) => setPermissionSearch(event.target.value)}
                placeholder="Search permissions"
              />
            </label>
            <PermissionPicker
              groups={permissionGroups}
              selected={createForm.permissions}
              search={permissionSearch}
              onChange={(permissions) => setCreateForm((form) => ({ ...form, permissions }))}
            />
            <button type="submit" className="report-submit-cta" disabled={modalSaving}>
              {modalSaving ? "Creating…" : "Create role"}
            </button>
          </form>
        </ModalBackdrop>
      ) : null}

      {editRole ? (
        <ModalBackdrop title={`Edit ${editRole.roleName}`} onClose={() => setEditRole(null)}>
          <form className="admin-users-form" onSubmit={handleEditSubmit}>
            {modalError ? <p className="admin-data-health-error">{modalError}</p> : null}
            <p className="muted">
              <strong>Code:</strong> {editRole.roleCode}
            </p>
            <label className="admin-users-field">
              <span>Role name</span>
              <input
                required
                value={editForm.roleName}
                onChange={(event) => setEditForm((form) => ({ ...form, roleName: event.target.value }))}
              />
            </label>
            <label className="admin-users-field">
              <span>Description</span>
              <textarea
                rows={3}
                value={editForm.description}
                onChange={(event) => setEditForm((form) => ({ ...form, description: event.target.value }))}
              />
            </label>
            <label className="admin-users-checkbox">
              <input
                type="checkbox"
                checked={editForm.isActive}
                onChange={(event) => setEditForm((form) => ({ ...form, isActive: event.target.checked }))}
              />
              Active
            </label>
            <button type="submit" className="report-submit-cta" disabled={modalSaving}>
              {modalSaving ? "Saving…" : "Save changes"}
            </button>
          </form>
        </ModalBackdrop>
      ) : null}

      {permissionsRole ? (
        <ModalBackdrop title={`Permissions — ${permissionsRole.roleName}`} wide onClose={() => setPermissionsRole(null)}>
          {permissionsReadOnly ? (
            <p className="muted admin-users-help">
              System roles are managed by the application seed process and cannot be edited here.
            </p>
          ) : null}
          {modalError ? <p className="admin-data-health-error">{modalError}</p> : null}
          <label className="admin-users-field">
            <span>Filter permissions</span>
            <input
              type="search"
              value={permissionSearch}
              onChange={(event) => setPermissionSearch(event.target.value)}
              placeholder="Search permissions"
            />
          </label>
          <PermissionPicker
            groups={permissionGroups}
            selected={permissionSelection}
            search={permissionSearch}
            disabled={permissionsReadOnly}
            onChange={(permissions) => {
              setPermissionSelection(permissions);
              setPermissionsDirty(true);
            }}
          />
          {!permissionsReadOnly ? (
            <button
              type="button"
              className="report-submit-cta"
              disabled={modalSaving || !permissionsDirty}
              onClick={handlePermissionsSave}
            >
              {modalSaving ? "Saving…" : "Save permissions"}
            </button>
          ) : null}
        </ModalBackdrop>
      ) : null}

      {detailRole ? (
        <ModalBackdrop title={detailRole.roleName} wide onClose={() => setDetailRole(null)}>
          <dl className="admin-users-detail">
            <div>
              <dt>Code</dt>
              <dd>
                <code>{detailRole.roleCode}</code>
              </dd>
            </div>
            <div>
              <dt>Type</dt>
              <dd>{detailRole.isSystemRole ? "System role" : "Custom role"}</dd>
            </div>
            <div>
              <dt>Status</dt>
              <dd>{detailRole.isActive === false ? "Inactive" : "Active"}</dd>
            </div>
            <div>
              <dt>Description</dt>
              <dd>{detailRole.description || "—"}</dd>
            </div>
            <div>
              <dt>Permissions</dt>
              <dd>{detailRole.permissions?.length ?? 0}</dd>
            </div>
            <div>
              <dt>Assigned users</dt>
              <dd>{detailRole.users?.length ?? 0}</dd>
            </div>
          </dl>
          {detailRole.permissions?.length ? (
            <section className="admin-roles-detail-list">
              <h3>Permissions</h3>
              <ul>
                {detailRole.permissions.map((permission) => (
                  <li key={permission.permissionCode}>
                    <strong>{permission.permissionName}</strong>
                    <span className="muted"> — {permission.permissionCode}</span>
                  </li>
                ))}
              </ul>
            </section>
          ) : null}
          {detailRole.users?.length ? (
            <section className="admin-roles-detail-list">
              <h3>Assigned users</h3>
              <div className="admin-data-health-table-wrap">
                <table className="admin-data-health-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Status</th>
                      <th>Last login</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detailRole.users.map((user) => (
                      <tr key={user.userId}>
                        <td>{user.displayName || user.email}</td>
                        <td>{user.email}</td>
                        <td>{user.isActive ? "Active" : "Inactive"}</td>
                        <td>{formatDateTime(user.lastLoginAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="muted admin-users-help">
                Assign roles to users from the Users admin screen.
              </p>
            </section>
          ) : (
            <p className="muted">No users are assigned to this role.</p>
          )}
        </ModalBackdrop>
      ) : null}
    </div>
  );
};

const AdminRolesPage = (props) => (
  <RequirePermission permission="roles.manage">
    <AdminRolesPageContent {...props} />
  </RequirePermission>
);

export default AdminRolesPage;
